/** @public */
export type PropertyControls<ComponentProps = any, ArrayTypes = any> = {
    [K in keyof ComponentProps]?: ControlDescription<Partial<ComponentProps>>
}

type Omit<T, K extends keyof T> = Pick<T, Exclude<keyof T, K>>
export type CompactControlsDescription<P = any> = NumberControlDescription<P> | EnumControlDescription<P>

export type FlatControlDescription<P = any> =
    | Omit<NumberControlDescription<P>, "hidden">
    | Omit<EnumControlDescription<P>, "hidden">
    | Omit<BooleanControlDescription<P>, "hidden">
    | Omit<StringControlDescription<P>, "hidden">
    | Omit<ColorControlDescription<P>, "hidden">
    | Omit<FusedNumberControlDescription<P>, "hidden">
    | Omit<SegmentedEnumControlDescription<P>, "hidden">
    | Omit<ImageControlDescription<P>, "hidden">
    | Omit<FileControlDescription<P>, "hidden">
    | Omit<ComponentInstanceDescription<P>, "hidden">

/** @public */
export type ControlDescription<P = any> =
    | NumberControlDescription<P>
    | EnumControlDescription<P>
    | BooleanControlDescription<P>
    | StringControlDescription<P>
    | ColorControlDescription<P>
    | FusedNumberControlDescription<P>
    | SegmentedEnumControlDescription<P>
    | ImageControlDescription<P>
    | FileControlDescription<P>
    | ComponentInstanceDescription<P>
    | ArrayControlDescription<P>

/**
 * Used by the {@link PropertyControls} and specifies the type of user interface for receiving
 * input. Each field has a distinct set of properties though they all accept `title` and `hidden`
 * properties.
 *
 * @remarks
 * ```javascript
 * addPropertyControls(MyComponent, {
 *   text: {
 *     type: ControlType.String,
 *     title: "Text",
 *     hidden: (props) => true
 *   },
 * }
 * ```
 * @public
 */
export const enum ControlType {
    /**
     * A property control that displays an on / off checkbox. The associated property will be `true` or `false`,
     * depending on the state of the checkbox. Includes an optional `defaultValue`, which is set to `true` by default. You can also customize the labels displayed in the property panel with the `enabledTitle` and `disabledTitle` properties.
     *
     * @remarks
     * ```javascript
     * addPropertyControls(MyComponent, {
     *   isCentered: {
     *     type: ControlType.Boolean,
     *     title: "Center",
     *     defaultValue: true,
     *     enabledTitle: "On",
     *     disabledTitle: "Off",
     *   },
     * })
     * ```
     */
    Boolean = "boolean",
    /**
     * A property control that accepts any numeric value. This will be provided directly as a property.
     * Will display an input field with a range slider by default. The
     * `displayStepper` option can be provided to include a stepper control instead.
     *
     * @remarks
     * ```javascript
     * addPropertyControls(MyComponent, {
     *   offset: {
     *     type: ControlType.Number,
     *     defaultValue: 5,
     *     min: 0,
     *     max: 10,
     *     unit: "px",
     *     step: 0.1,
     *     displayStepper: true,
     *   },
     * })
     * ```
     */
    Number = "number",
    /**
     * A property control that accepts plain text values. This will be provided directly as a property.
     * Will display an input field with an optional placeholder value.
     *
     * @remarks
     * ```javascript
     * addPropertyControls(MyComponent, {
     *   offset: {
     *     type: ControlType.String,
     *     defaultValue: "Framer X",
     *     placeholder: "Type something…",
     *   },
     * }
     * ```
     */
    String = "string",
    /**
     * A property control that can be used to take a single number or four distinct numeric input fields.
     * The typical use case for this control is for visual properties like border, padding or margin.
     * It will display an input field to accept a single value, alongside a segmented control
     * allowing four distinct values to be provided.
     *
     * ```javascript
     * addPropertyControls(MyComponent, {
     *   title: {
     *     type: ControlType.FusedNumber,
     *     defaultValue: 0,
     *     toggleKey: "isPerSide",
     *     valueKeys: ["top", "left", "right", "bottom"],
     *     valueLabels: ["T", "L", "R", "B"],
     *     min: 0,
     *   },
     * })
     * ```
     */
    FusedNumber = "fusednumber",
    /**
     * A property control that represents a list of options. The selected option will be provided as a property.
     * This control is displayed as a dropdown menu in which a user can select one of the items.
     *
     * ```javascript
     * addPropertyControls(MyComponent, {
     *   value: {
     *     type: ControlType.Enum,
     *     defaultValue: "a",
     *     options: ["a", "b", "c"],
     *     optionTitles: ["Option A", "Option B", "Option C"],
     *   },
     * })
     * ```
     */
    Enum = "enum",
    /**
     * A property control that represents a list of option. The selected option will be provided as a property.
     * This control is displayed as a segmented control. Otherwise, it behaves exactly the
     * same as {@link ControlType.Enum}.
     *
     * @remarks
     * ```javascript
     * addPropertyControls(MyComponent, {
     *   value: {
     *     type: ControlType.SegmentedEnum,
     *     defaultValue: "a",
     *     options: ["a", "b", "c"],
     *     optionTitles: ["Option A", "Option B", "Option C"],
     *   },
     * })
     * ```
     */
    SegmentedEnum = "segmentedenum",
    /**
     * A property control that represents a color value. It will be provided as a string via properties.
     * This control is displayed as a color field and will provide the selected color in either
     * HEX (`"#fff"`) or HSL (`hsla(203, 87%, 50%, 0.5)`) notation, depending on
     * whether there is an alpha channel.
     *
     * @remarks
     * ```javascript
     * addPropertyControls(MyComponent, {
     *   background: {
     *     type: ControlType.Color,
     *     defaultValue: "#fff",
     *   },
     * })
     * ```
     */
    Color = "color",
    /**
     * A property control that represents an image resource. It will be provided as an URL via properties.
     * Displayed as an image picker with associated file picker. The chosen asset
     * will be provided as a fully qualified URL.
     *
     * @remarks
     * ```jsx
     * addPropertyControls(MyComponent, {
     *   avatar: {
     *     type: ControlType.Image,
     *   }
     * })
     * ```
     */
    Image = "image",
    /**
     * A property control that represents a resource. It will be provided as an URL via properties.
     * Displayed as an file picker that will open a native file browser. The
     * selected file will be provided as a fully qualified URL. The
     * `allowedFileTypes` property must be provided to specify acceptable file
     * types.
     *
     * @remarks
     * ```javascript
     * addPropertyControls(MyComponent, {
     *   asset: {
     *     type: ControlType.File,
     *     allowedFileTypes: ["pdf"],
     *   },
     * })
     * ```
     */
    File = "file",
    /**
     * A property control that references to another components on the canvas, provided via a property.
     * The component will have an outlet to allow linking to other Frames.
     * Available Frames will also be displayed in a dropdown menu in the
     * properties panel. The component reference will be provided as a property.
     * As a convention, the name for the property is usually just `children`.
     *
     * Multiple components can be linked by combining the `ComponentInstance`
     * type with the {@link ControlType.Array}.
     *
     * ```javascript
     * addPropertyControls(MyComponent, {
     *   children: {
     *     type: ControlType.ComponentInstance,
     *   },
     * })
     * ```
     */
    ComponentInstance = "componentinstance",
    /**
     * A property control that allows multiple values per `ControlType`, provided as an array via properties.
     * For most control types this will be displayed as an additional
     * section in the properties panel allowing as many fields to be provided
     * as required.
     *
     * For a {@link ControlType.ComponentInstance} the Frame will also gain
     * an additional outlet control on the Canvas that allows links to be created between frames.
     *
     * ```javascript
     * addPropertyControls(MyComponent, {
     *   images: {
     *     type: ControlType.Array,
     *     propertyControl: {
     *       type: ControlType.Image
     *     }
     *   },
     *   children: {
     *     type: ControlType.Array,
     *     propertyControl: {
     *       type: ControlType.ComponentInstance
     *     }
     *   },
     * })
     * ```
     */
    Array = "array",
}

export interface BaseControlDescription<P = any> {
    title?: string
    hidden?(props: P): boolean
}

export interface BooleanControlDescription<P = any> extends BaseControlDescription<P> {
    type: ControlType.Boolean
    defaultValue?: boolean
    disabledTitle?: string
    enabledTitle?: string
}

export interface NumberControlDescription<P = any> extends BaseControlDescription<P> {
    type: ControlType.Number
    defaultValue?: number
    max?: number
    min?: number
    unit?: string
    step?: number
    displayStepper?: boolean
}

export interface StringControlDescription<P = any> extends BaseControlDescription<P> {
    type: ControlType.String
    defaultValue?: string
    placeholder?: string
}

export interface FusedNumberControlDescription<P = any> extends BaseControlDescription<P> {
    type: ControlType.FusedNumber
    defaultValue?: number
    toggleKey: keyof P
    toggleTitles: [string, string]
    valueKeys: [keyof P, keyof P, keyof P, keyof P]
    valueLabels: [string, string, string, string]
    min?: number
}

export interface DeprecatedFusedNumberControlDescription<P = any> extends BaseControlDescription<P> {
    type: ControlType.FusedNumber
    valueKeys: [keyof P, keyof P, keyof P, keyof P]
    valueLabels: [string, string, string, string]
    min?: number
    // deprecated
    splitKey: keyof P
    splitLabels: [string, string]
}

export interface EnumControlDescription<P = any> extends BaseControlDescription<P> {
    type: ControlType.Enum
    defaultValue?: string
    options: string[]
    optionTitles?: string[] | ((props: P | null) => string[])
}

export interface SegmentedEnumControlDescription<P = any> extends BaseControlDescription<P> {
    type: ControlType.SegmentedEnum
    defaultValue?: string
    options: string[]
    optionTitles?: string[] | ((props: P | null) => string[])
}

export interface ColorControlDescription<P = any> extends BaseControlDescription<P> {
    type: ControlType.Color
    defaultValue?: string
}

export interface ImageControlDescription<P = any> extends BaseControlDescription<P> {
    type: ControlType.Image
}

export interface FileControlDescription<P = any> extends BaseControlDescription<P> {
    type: ControlType.File
    allowedFileTypes: string[]
}

export interface ComponentInstanceDescription<P = any> extends BaseControlDescription<P> {
    type: ControlType.ComponentInstance
}

/** @public */
export interface ArrayControlDescription<P = any> extends BaseControlDescription<P> {
    type: ControlType.Array
    propertyControl: FlatControlDescription<P>
    maxCount?: number
    defaultValue?: any[]
}
